import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { ChannelContent } from './types';
import { ArrowGlyph, delay, SectionHeading, Stage, tone } from '../primitives';
import { useReveal } from '../useReveal';

/** How to get the agent onto the channel, next to the window you do it in. */
export default function ChannelSetup({
  setup,
}: {
  setup: ChannelContent['setup'];
}) {
  const { ref, state } = useReveal<HTMLDivElement>(0.2);

  return (
    <section id='setup' className='py-6 sm:py-10'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='100% 100%'
          halftoneReach={60}
          className='px-5 py-14 sm:px-12 sm:py-20'
        >
          <SectionHeading
            className='px-1'
            title={setup.title}
            tail={setup.tail}
          />

          <div className='mt-14 grid gap-14 lg:grid-cols-12 lg:items-center lg:gap-12'>
            <div className='min-w-0 px-1 lg:col-span-5'>
              <ol className='space-y-7'>
                {setup.steps.map(({ title, detail }, index) => (
                  <li key={title} className='relative flex gap-4'>
                    {index < setup.steps.length - 1 && (
                      <span
                        aria-hidden='true'
                        className='absolute start-4 top-10 h-[calc(100%-1rem)] w-px -translate-x-1/2 bg-primary-600/20 rtl:translate-x-1/2 dark:bg-primary-400/25'
                      />
                    )}
                    <span className='relative flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-white shadow-[0_8px_18px_-8px_rgba(79,70,229,0.8)]'>
                      {index + 1}
                    </span>
                    <div className='min-w-0 pt-0.5'>
                      <h3
                        className={clsxm('text-[17px] font-semibold', tone.ink)}
                      >
                        {title}
                      </h3>
                      <p
                        className={clsxm(
                          'mt-1.5 text-[15px] leading-7',
                          tone.body,
                        )}
                      >
                        {detail}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>

              {/* Inline, so a label that wraps keeps its arrow beside it. */}
              <a
                href={setup.guide}
                className='group ms-12 mt-8 inline-block text-sm font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
              >
                {setup.guideLabel}
                <ArrowGlyph className='ms-1.5 inline-block h-3.5 w-3.5 align-[-2px] transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
              </a>
            </div>

            <div
              ref={ref}
              data-reveal={state}
              className={clsxm(styles.reveal, 'min-w-0 lg:col-span-7')}
            >
              <p className='sr-only'>{setup.description}</p>
              <div aria-hidden='true' className={styles.rv} style={delay(0)}>
                {setup.visual}
              </div>
            </div>
          </div>
        </Stage>
      </div>
    </section>
  );
}
